import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useStore } from '../store';
import { refreshSession, useSession } from '../lib/session';
import { safeConversionPath, trialError } from '../lib/sessionFlow';
import { getUploadProgress, isUploadReady } from './uploadProgress';
import {
  DOCUMENT_TYPE_OPTIONS,
  REGULATION_SCOPE_LABELS,
  REGULATION_SCOPE_OPTIONS,
  type DocumentType,
  type RegulationScope,
  type Organization,
  type Client,
  type OrganizationListResponse,
  type ClientListResponse,
  type Project,
  type ProjectTemplate,
  type ProjectTemplateListResponse,
} from '../types';

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

export default function UploadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scans } = useStore();
  const session = useSession(state => state.state);

  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<DocumentType | ''>('');
  const [regulationScope, setRegulationScope] = useState<RegulationScope | ''>(
    ''
  );
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [organizationId, setOrganizationId] = useState('');
  const [clientId, setClientId] = useState('');
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [templateProjectName, setTemplateProjectName] = useState('');
  const [isDocumentTypeHelpOpen, setIsDocumentTypeHelpOpen] = useState(false);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isStartingFromTemplate, setIsStartingFromTemplate] = useState(false);
  const uploadProgressInput = {
    hasFile: Boolean(file),
    hasDocumentType: Boolean(documentType),
    hasRegulationScope: Boolean(regulationScope),
  };
  const uploadProgress = getUploadProgress(uploadProgressInput);
  const uploadReady = isUploadReady(uploadProgressInput);

  useEffect(() => {
    api
      .get<OrganizationListResponse>('/api/organizations')
      .then(response => {
        setOrganizations(response.data.organizations);
        const personal = response.data.organizations.find(
          item => item.isPersonal
        );
        setOrganizationId(
          current =>
            current || personal?.id || response.data.organizations[0]?.id || ''
        );
      })
      .catch(() => setError('Workspaces could not be loaded.'));
  }, []);

  useEffect(() => {
    setClientId('');
    if (!organizationId) {
      setClients([]);
      return;
    }
    api
      .get<ClientListResponse>(`/api/organizations/${organizationId}/clients`)
      .then(response => {
        setClients(
          response.data.clients.filter(client => client.status === 'active')
        );
      })
      .catch(() => setError('Clients could not be loaded.'));
  }, [organizationId]);

  useEffect(() => {
    setTemplateId('');
    if (!organizationId) {
      setTemplates([]);
      return;
    }
    api
      .get<ProjectTemplateListResponse>(
        `/api/organizations/${organizationId}/templates`
      )
      .then(response => setTemplates(response.data.templates))
      .catch(() => setTemplates([]));
  }, [organizationId]);

  const onDrop = useCallback(
    (accepted: File[], rejected: unknown[]) => {
      setError('');
      if (rejected && (rejected as unknown[]).length > 0) {
        setError(t('upload.errors.pdfOnly'));
        return;
      }

      const nextFile = accepted[0];
      if (!nextFile) return;

      if (nextFile.size > 50 * 1024 * 1024) {
        setError(t('upload.errors.tooLarge'));
        return;
      }

      setFile(nextFile);
    },
    [t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    multiple: false,
  });

  const handleScan = async () => {
    if (
      session?.principalKind === 'anonymous' &&
      session.limit > 0 &&
      session.exhausted
    ) {
      navigate(safeConversionPath(session.conversionPath));
      return;
    }
    if (!file) return;
    if (!documentType) {
      setError(t('upload.errors.documentTypeRequired'));
      return;
    }
    if (!regulationScope) {
      setError(t('upload.errors.scopeRequired'));
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('pdf', file);
      formData.append('documentType', documentType);
      formData.append('regulationScope', regulationScope);
      if (organizationId) formData.append('organizationId', organizationId);
      if (clientId) formData.append('clientId', clientId);

      const res = await api.post<{ id: string; projectId: string }>(
        '/api/scans',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      await refreshSession().catch(() => {});
      navigate(`/projects/${res.data.projectId}`);
    } catch (err: unknown) {
      const quota = trialError(err);
      if (quota.code === 'free_scan_limit_reached') {
        if (quota.trial) useSession.setState({ state: quota.trial });
        navigate(
          safeConversionPath(
            quota.conversionPath ||
              quota.trial?.conversionPath ||
              session?.conversionPath
          )
        );
        setIsUploading(false);
        return;
      }
      console.error(err);
      const errorResponse = err as { response?: { data?: { error?: string } } };
      setError(errorResponse?.response?.data?.error ?? t('errors.generic'));
      setIsUploading(false);
    }
  };

  const startFromTemplate = async () => {
    if (!templateId || !templateProjectName.trim()) return;
    setIsStartingFromTemplate(true);
    setError('');
    try {
      const response = await api.post<Project>(
        `/api/templates/${templateId}/projects`,
        { name: templateProjectName.trim(), clientId }
      );
      navigate(`/projects/${response.data.id}`);
    } catch (err: unknown) {
      const errorResponse = err as { response?: { data?: { error?: string } } };
      setError(
        errorResponse?.response?.data?.error ??
          'The project could not be created from this template.'
      );
      setIsStartingFromTemplate(false);
    }
  };

  return (
    <div className="upload-page">
      {session?.principalKind === 'anonymous' &&
        session.limit > 0 &&
        session.nextScanIsLast && (
          <p role="status" className="alert alert-warning">
            <strong>{t('trial.lastScan')}</strong>
          </p>
        )}
      <div className="animate-fade-in">
        <div className="page-intro">
          <div className="page-eyebrow">
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                background: 'var(--accent-primary)',
                display: 'inline-block',
              }}
            />
            {t('upload.eyebrow')}
          </div>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 36,
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
              marginBottom: 10,
            }}
          >
            {t('upload.title')}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 16 }}>
            {t('upload.subtitle')}
          </p>
        </div>

        <div
          {...getRootProps()}
          className="upload-dropzone"
          style={{
            background: isDragActive ? 'var(--accent-dim)' : 'var(--bg-card)',
            border: `2px dashed ${isDragActive ? 'var(--accent-primary)' : file ? 'var(--success)' : 'var(--border)'}`,
            borderRadius: 'var(--radius-lg)',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: isDragActive
              ? 'var(--accent-glow)'
              : 'var(--shadow-card)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {isDragActive && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                height: 2,
                background:
                  'linear-gradient(90deg, transparent, var(--accent-primary), transparent)',
                animation: 'scanLine 1s linear infinite',
              }}
            />
          )}

          <input {...getInputProps()} />

          {(file || isDragActive) && (
            <div style={{ fontSize: 52, marginBottom: 16 }}>
              {file ? '📄' : '📥'}
            </div>
          )}

          <p
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 20,
              fontWeight: 600,
              color: isDragActive
                ? 'var(--accent-primary)'
                : 'var(--text-primary)',
              marginBottom: 8,
            }}
          >
            {isDragActive ? t('upload.dropzoneActive') : t('upload.dropzone')}
          </p>
          <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>
            {t('upload.dropzoneHint')}
          </p>
        </div>

        {file && (
          <div
            className="animate-slide-up upload-file-banner"
            style={{
              marginTop: 16,
              padding: '16px 20px',
              background: 'var(--success-dim)',
              border: '1px solid rgba(0, 230, 118, 0.2)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 24 }}>✅</span>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    fontSize: 14,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {file.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {formatBytes(file.size)}
                </div>
              </div>
            </div>
            <button
              onClick={event => {
                event.stopPropagation();
                setFile(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                fontSize: 18,
                padding: 4,
                alignSelf: 'flex-start',
              }}
            >
              ✕
            </button>
          </div>
        )}

        <section
          style={{
            marginTop: 18,
            padding: '18px 20px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 16,
              alignItems: 'flex-start',
              marginBottom: 14,
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label
                  htmlFor="documentType"
                  style={{
                    display: 'block',
                    fontFamily: 'var(--font-display)',
                    fontSize: 15,
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    marginBottom: 4,
                  }}
                >
                  {t('upload.documentType.title')}
                </label>
                <button
                  type="button"
                  onClick={() => setIsDocumentTypeHelpOpen(true)}
                  aria-haspopup="dialog"
                  style={{
                    border: 'none',
                    background: 'none',
                    color: 'var(--accent-primary)',
                    cursor: 'pointer',
                    fontSize: 13,
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  {t('upload.documentType.help.trigger')}
                </button>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: 'var(--text-muted)',
                  lineHeight: 1.5,
                }}
              >
                {t('upload.documentType.hint')}
              </p>
            </div>
            <span
              style={{
                fontSize: 11,
                color: 'var(--warning)',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                flexShrink: 0,
              }}
            >
              {t('upload.documentType.required')}
            </span>
          </div>

          <select
            id="documentType"
            value={documentType}
            onChange={event =>
              setDocumentType(event.target.value as DocumentType | '')
            }
            required
            style={{
              width: '100%',
              minHeight: 48,
              padding: '12px 14px',
              background: 'var(--bg-secondary)',
              border: `1px solid ${
                documentType ? 'rgba(0,200,255,0.35)' : 'var(--border)'
              }`,
              borderRadius: 'var(--radius-sm)',
              color: documentType ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: 14,
            }}
          >
            <option value="">{t('upload.documentType.placeholder')}</option>
            {DOCUMENT_TYPE_OPTIONS.map(type => (
              <option key={type} value={type}>
                {t(`upload.documentType.options.${type}`)}
              </option>
            ))}
          </select>

          {isDocumentTypeHelpOpen && (
            <dialog
              open
              aria-labelledby="document-type-help-title"
              onCancel={() => setIsDocumentTypeHelpOpen(false)}
              style={{
                maxWidth: 640,
                width: 'calc(100% - 32px)',
                maxHeight: '80vh',
                overflowY: 'auto',
                padding: 24,
                background: 'var(--bg-card)',
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-lg)',
                color: 'var(--text-primary)',
                boxShadow: 'var(--shadow-card)',
              }}
            >
              <h2
                id="document-type-help-title"
                style={{ fontFamily: 'var(--font-display)', marginTop: 0 }}
              >
                {t('upload.documentType.help.title')}
              </h2>
              <ul style={{ display: 'grid', gap: 12, paddingLeft: 20 }}>
                {DOCUMENT_TYPE_OPTIONS.map(type => (
                  <li key={type} style={{ color: 'var(--text-secondary)' }}>
                    <strong style={{ color: 'var(--text-primary)' }}>
                      {t(`upload.documentType.options.${type}`)}:
                    </strong>{' '}
                    {t(`upload.documentType.help.descriptions.${type}`)}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="button button-secondary"
                onClick={() => setIsDocumentTypeHelpOpen(false)}
                style={{ marginTop: 8 }}
              >
                {t('upload.documentType.help.close')}
              </button>
            </dialog>
          )}
        </section>

        <section
          style={{
            marginTop: 18,
            padding: '18px 20px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 16,
              alignItems: 'flex-start',
              marginBottom: 14,
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 15,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 4,
                }}
              >
                {t('upload.regulationScope.title')}
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: 'var(--text-muted)',
                  lineHeight: 1.5,
                }}
              >
                {t('upload.regulationScope.hint')}
              </p>
            </div>
            <span
              style={{
                fontSize: 11,
                color: 'var(--warning)',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                flexShrink: 0,
              }}
            >
              {t('upload.regulationScope.required')}
            </span>
          </div>

          <div style={{ display: 'grid', gap: 10 }}>
            {REGULATION_SCOPE_OPTIONS.map(scope => {
              const selected = regulationScope === scope;
              return (
                <label
                  key={scope}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 14px',
                    minHeight: 48,
                    background: selected
                      ? 'var(--accent-dim)'
                      : 'var(--bg-secondary)',
                    border: `1px solid ${
                      selected ? 'rgba(0,200,255,0.35)' : 'var(--border)'
                    }`,
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="regulationScope"
                    value={scope}
                    checked={selected}
                    onChange={() => setRegulationScope(scope)}
                    style={{ flexShrink: 0 }}
                  />
                  <span
                    style={{
                      color: selected
                        ? 'var(--accent-primary)'
                        : 'var(--text-secondary)',
                      fontSize: 14,
                      fontWeight: selected ? 700 : 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {REGULATION_SCOPE_LABELS[scope]}
                  </span>
                </label>
              );
            })}
          </div>
          <p
            style={{
              margin: '12px 0 0',
              color: 'var(--text-muted)',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {t('upload.transitionNotice')}
          </p>
        </section>

        {organizations.length > 0 && (
          <section
            style={{
              marginTop: 18,
              padding: '18px 20px',
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 16,
              }}
            >
              <label style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                {t('workspace.workspace')}
                <select
                  value={organizationId}
                  onChange={event => setOrganizationId(event.target.value)}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 8,
                    padding: 10,
                    background: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  {organizations.map(organization => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                {t('workspace.clientOptional')}
                <select
                  value={clientId}
                  onChange={event => setClientId(event.target.value)}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 8,
                    padding: 10,
                    background: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  <option value="">{t('workspace.noClient')}</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>
        )}

        {templates.length > 0 && (
          <section
            style={{
              marginTop: 18,
              padding: '18px 20px',
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <h2
              style={{
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-display)',
                fontSize: 18,
                margin: 0,
              }}
            >
              Start from a template
            </h2>
            <p
              style={{
                color: 'var(--text-secondary)',
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              Create a project with a reusable checklist, evidence categories,
              tasks, and guidance—without uploading a source document first.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 12,
                alignItems: 'end',
              }}
            >
              <label style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                Template
                <select
                  value={templateId}
                  onChange={event => setTemplateId(event.target.value)}
                  style={workspaceInput}
                >
                  <option value="">Choose a template</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                Project name
                <input
                  value={templateProjectName}
                  onChange={event => setTemplateProjectName(event.target.value)}
                  style={workspaceInput}
                />
              </label>
              <button
                className="button button-secondary"
                onClick={() => void startFromTemplate()}
                disabled={
                  isStartingFromTemplate ||
                  !templateId ||
                  !templateProjectName.trim()
                }
              >
                {isStartingFromTemplate
                  ? 'Starting project…'
                  : 'Start project from template'}
              </button>
            </div>
          </section>
        )}

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: '12px 16px',
              background: 'var(--danger-dim)',
              border: '1px solid rgba(255,23,68,0.2)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--danger)',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <span>{error}</span>
            {file && (
              <button
                className="button button-secondary"
                style={{
                  minHeight: 'auto',
                  padding: '6px 12px',
                  fontSize: 12,
                  flexShrink: 0,
                }}
                onClick={() => {
                  setError('');
                  handleScan();
                }}
              >
                Retry
              </button>
            )}
          </div>
        )}

        <section
          aria-label={t('upload.progress.title')}
          style={{ marginTop: 18 }}
        >
          <p
            style={{
              color: 'var(--text-secondary)',
              fontSize: 13,
              margin: '0 0 8px',
            }}
          >
            {t('upload.progress.title')}
          </p>
          <div style={{ display: 'grid', gap: 6 }}>
            {uploadProgress.map(step => (
              <div
                key={step.id}
                style={{
                  color: step.complete ? 'var(--success)' : 'var(--text-muted)',
                  fontSize: 14,
                }}
              >
                <span aria-hidden="true">{step.complete ? '✓' : '○'}</span>{' '}
                {t(`upload.progress.${step.id}`)}
              </div>
            ))}
          </div>
        </section>

        <div className="upload-actions">
          <button
            onClick={handleScan}
            disabled={!uploadReady || isUploading}
            className="button button-primary"
            style={{
              flex: 1,
              padding: '16px 32px',
              fontSize: 16,
              fontFamily: 'var(--font-display)',
              letterSpacing: '0.02em',
              boxShadow: !uploadReady
                ? 'none'
                : '0 0 24px rgba(0, 200, 255, 0.35)',
            }}
          >
            {isUploading ? '⟳ Uploading…' : `🔍 ${t('upload.scanButton')}`}
          </button>
        </div>

        {scans.length > 0 && (
          <div style={{ marginTop: 48 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-muted)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                marginBottom: 14,
              }}
            >
              {t('upload.recentScans')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {scans.slice(0, 3).map(scan => (
                <button
                  key={scan.id}
                  onClick={() => navigate(`/scan/${scan.id}/results`)}
                  className="recent-scan-row"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    textAlign: 'left',
                  }}
                >
                  <span
                    style={{
                      fontSize: 14,
                      color: 'var(--text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {scan.filename}
                  </span>
                  {scan.status === 'done' && (
                    <span
                      style={{
                        color: 'var(--text-muted)',
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t('upload.findingCount', {
                        count: scan.findings?.length ?? scan.risks.length,
                      })}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const workspaceInput: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 8,
  padding: 10,
  background: 'var(--bg-tertiary)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
};
